import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cuit", "externalId", "alert"]
  static values = {
    patientId: Number,
    checkUrl: String
  }

  connect() {
    // Debounce para evitar múltiples llamadas mientras el usuario escribe
    this.timeout = null
  }

  checkCuit() {
    const cuit = this.cuitTarget.value.trim()
    const cuitDigits = cuit.replace(/\D/g, '')

    // Si el CUIT está vacío o tiene menos de 11 dígitos, no hacer nada
    if (cuitDigits.length < 11) {
      this.hideAlert()
      if (this.timeout) {
        clearTimeout(this.timeout)
        this.timeout = null
      }
      return
    }

    // Limpiar timeout anterior
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Esperar 1.5 segundos después de que el usuario deje de escribir
    // Solo si el CUIT tiene al menos 11 dígitos
    this.timeout = setTimeout(() => {
      // Verificar nuevamente que el CUIT siga siendo válido antes de buscar
      const currentCuit = this.cuitTarget.value.trim()
      const currentCuitDigits = currentCuit.replace(/\D/g, '')
      if (currentCuitDigits.length >= 11) {
        this.performCheck()
      }
    }, 1500)
  }

  // También buscar cuando el campo pierde el foco (blur)
  checkCuitOnBlur() {
    const cuit = this.cuitTarget.value.trim()
    const cuitDigits = cuit.replace(/\D/g, '')

    // Si el CUIT tiene al menos 11 dígitos, buscar inmediatamente
    if (cuitDigits.length >= 11) {
      // Cancelar cualquier timeout pendiente
      if (this.timeout) {
        clearTimeout(this.timeout)
        this.timeout = null
      }
      this.performCheck()
    } else {
      // Si está vacío o incompleto, ocultar alertas
      this.hideAlert()
    }
  }

  async performCheck() {
    const cuit = this.cuitTarget.value.trim()

    // Validar que el CUIT tenga al menos 11 dígitos
    const cuitDigits = cuit.replace(/\D/g, '')
    if (cuitDigits.length < 11) {
      this.hideAlert()
      return
    }

    // Mostrar indicador de carga
    this.showLoading()

    try {
      // Construir URL según si es nuevo paciente o edición
      let url
      if (this.patientIdValue) {
        url = `/patients/${this.patientIdValue}/check_colppy_client?cuit=${encodeURIComponent(cuit)}&patient_id=${this.patientIdValue}`
      } else {
        url = `/patients/check_colppy_client?cuit=${encodeURIComponent(cuit)}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        credentials: 'same-origin'
      })

      // Verificar si la respuesta es exitosa
      if (!response.ok) {
        // Intentar parsear el JSON del error
        let errorData
        try {
          errorData = await response.json()
        } catch (e) {
          errorData = { message: `Error HTTP ${response.status}` }
        }
        this.showError(errorData.message || 'Error al verificar el CUIT en Colppy')
        return
      }

      const data = await response.json()

      if (data.success) {
        if (data.found) {
          // Cliente encontrado
          if (this.hasExternalIdTarget) {
            this.externalIdTarget.value = data.client_id
            // Disparar evento personalizado para que el script del modal detecte el cambio
            this.externalIdTarget.dispatchEvent(new Event('input', { bubbles: true }))
            this.externalIdTarget.dispatchEvent(new Event('change', { bubbles: true }))
          }
          this.showSuccess(data.message, data.client_data)
        } else if (data.created) {
          // Cliente creado exitosamente en Colppy
          if (this.hasExternalIdTarget) {
            this.externalIdTarget.value = data.client_id
            // Disparar evento personalizado para que el script del modal detecte el cambio
            this.externalIdTarget.dispatchEvent(new Event('input', { bubbles: true }))
            this.externalIdTarget.dispatchEvent(new Event('change', { bubbles: true }))
          }
          // Mostrar notificación flash además del alert
          this.showFlashNotification("Cliente creado exitosamente en Colppy", "success")
          this.showSuccess(data.message || "Cliente creado exitosamente en Colppy")
        } else {
          // No encontrado, se creará al guardar - esto es normal, mostrar info
          this.showInfo(data.message)
        }
      } else {
        // Error real - mostrar mensaje de error
        if (data.error) {
          this.showError(data.message || 'Ocurrió un error al verificar el CUIT en Colppy')
        } else {
          this.showError(data.message || 'Error al verificar el CUIT en Colppy')
        }
      }
    } catch (error) {
      console.error('Error checking Colppy:', error)
      this.showError('Ocurrió un error al verificar el CUIT en Colppy. Por favor, intente nuevamente.')
    }
  }

  showLoading() {
    this.hideAlert()
    const alertDiv = document.createElement('div')
    alertDiv.className = 'alert alert-info alert-dismissible fade show mt-2'
    alertDiv.setAttribute('data-colppy-cuit-check-target', 'alert')
    alertDiv.innerHTML = `
      <i class="fas fa-spinner fa-spin mr-2"></i>
      Verificando CUIT en Colppy...
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    `
    this.cuitTarget.parentElement.appendChild(alertDiv)
  }

  showSuccess(message, clientData = null) {
    this.hideAlert()
    let alertContent = `
      <i class="fas fa-check-circle mr-2"></i>
      <strong>${message}</strong>
    `

    if (clientData) {
      alertContent += `
        <br><small>
          <strong>Razón Social:</strong> ${clientData.razon_social || 'N/A'} |
          <strong>Nombre Fantasía:</strong> ${clientData.nombre_fantasia || 'N/A'}
        </small>
      `
    }

    alertContent += `
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    `

    const alertDiv = document.createElement('div')
    alertDiv.className = 'alert alert-success alert-dismissible fade show mt-2'
    alertDiv.setAttribute('data-colppy-cuit-check-target', 'alert')
    alertDiv.innerHTML = alertContent
    this.cuitTarget.parentElement.appendChild(alertDiv)
  }

  showInfo(message) {
    this.hideAlert()
    const alertDiv = document.createElement('div')
    alertDiv.className = 'alert alert-info alert-dismissible fade show mt-2'
    alertDiv.setAttribute('data-colppy-cuit-check-target', 'alert')
    alertDiv.innerHTML = `
      <i class="fas fa-info-circle mr-2"></i>
      ${message}
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    `
    this.cuitTarget.parentElement.appendChild(alertDiv)
  }

  showError(message) {
    this.hideAlert()
    const alertDiv = document.createElement('div')
    alertDiv.className = 'alert alert-warning alert-dismissible fade show mt-2'
    alertDiv.setAttribute('data-colppy-cuit-check-target', 'alert')
    alertDiv.innerHTML = `
      <i class="fas fa-exclamation-triangle mr-2"></i>
      ${message}
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    `
    this.cuitTarget.parentElement.appendChild(alertDiv)
  }

  hideAlert() {
    const existingAlert = this.element.querySelector('[data-colppy-cuit-check-target="alert"]')
    if (existingAlert) {
      existingAlert.remove()
    }
  }

  showFlashNotification(message, type = 'success') {
    const flashKey = type === 'success' ? 'success' : 'danger'
    const notification = document.createElement('div')

    notification.className = `flash flash-${flashKey} alert alert-dismissible fade show`
    notification.setAttribute('role', 'alert')
    notification.setAttribute('data-flash-key', flashKey)
    notification.setAttribute('data-controller', 'flash')
    notification.setAttribute('data-flash-auto-dismiss-value', 'true')
    notification.setAttribute('data-flash-dismiss-delay-value', '5000')
    notification.style.cssText = 'z-index: 9999; position: fixed; top: 20px; right: 20px; min-width: 300px;'

    const icon = type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'
    notification.innerHTML = `
      <i class="fas ${icon} mr-2"></i>
      <span class="w-100 text-center">${message}</span>
      <a data-dismiss="alert" aria-label="Close" class="text-secondary ml-2">
        <i class="fas fa-times"></i>
      </a>
    `

    document.body.appendChild(notification)

    // Auto-remover después de 5 segundos si el flash controller no lo hace
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove()
      }
    }, 5000)
  }
}
